import { useEffect, useState } from "react";
import { useApiContext } from 'context/ApiContext';

// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import PageLayout from "examples/LayoutContainers/PageLayout";
import ProfileInfoCard from "examples/Cards/InfoCards/ProfileInfoCard";

import ProfileDashboardNavbar from "examples/Navbars/DashboardNavbar";

// Overview page components
import Header from "layouts/profile/components/Header";

import Card from "@mui/material/Card";
import DataTable from "examples/Tables/DataTableV2";

function Overview() {
    const { backEndVersionPro, currentUserPro, currentUserParksPro, APIRequestType, getAPI, postAPI } = useApiContext();

    const [formValues, setFormValues] = useState({});
    const [initialValues, setInitialValues] = useState({});
    const [error, setError] = useState(false); // Etat pour gerer l'erreur visuelle

    const [phoneError, setPhoneError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [newPswdError, setNewPswdError] = useState(false);
    const [confirmPswdError, setConfirmPswdError] = useState(false);

    useEffect(() => {
        console.log(" 0 0 2   Pro");
        getAPI(APIRequestType.GET_CURRENT_USER);
        getAPI(APIRequestType.GET_BACKEND_VERSION);
    }, []);

    useEffect(() => {
        if (currentUserPro) {
            const formattedPhone = formatPhoneNumber(currentUserPro.phone);
            const formData = {
                lastName: currentUserPro.lastName || "",
                name: currentUserPro.name || "",
                phone: formattedPhone,
                email: currentUserPro.email || "",
                newPswd: "",
                confirmNewPswd: "",
            };

            setFormValues(formData);
            setInitialValues(formData);
        }
    }, [currentUserPro]);

    const formatPhoneNumber = (phone) => {
        if (!phone) return "";

        // Supprimer tous les caractères non numériques
        let cleaned = phone.replace(/\D/g, "");
        // Si le numéro commence par 0033, on retire les 4 premiers caractères
        if (cleaned.startsWith("0033")) {
            cleaned = cleaned.slice(4);
        }
        // S'assurer de ne garder que les 9 derniers chiffres
        cleaned = cleaned.slice(-9);
        return cleaned;
    };

    const isValidEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const isStrongPassword = (password) => {
        const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
        return regex.test(password);
    };

    const isFormValid = () => {
        const {
            lastName,
            name,
            phone,
            email,
            newPswd,
            confirmNewPswd
        } = formValues;

        const fieldsFilled =
            lastName &&
            name &&
            phone &&
            email;

        const noValidationErrors =
            !emailError &&
            !phoneError &&
            !newPswdError &&
            !confirmPswdError;

        const fieldsModified = Object.keys(formValues).some((key) =>
            key !== "parkAccess" && formValues[key] !== initialValues[key]
        );
        const passwordsMatch = newPswd === confirmNewPswd;

        return fieldsModified && passwordsMatch && fieldsFilled && noValidationErrors;
    };

    const handleChange = (field) => (e) => {
        let value = e.target.value;

        if (field === "lastName") {
            // Garder uniquement les lettres, lettres accentuées et le tiret
            value = value.replace(/[^a-zA-ZÀ-ÿ\s-]/g, "");
            // Limiter à 25 caractères
            value = value.slice(0, 25);
            // Capitaliser la première lettre de chaque mot
            value = value.toUpperCase();
        }

        if (field === "name") {
            value = value.replace(/[^a-zA-ZÀ-ÿ\s-]/g, "");
            value = value.slice(0, 25);
            value = value.replace(/(?:^|[\s-])\S/g, match => match.toUpperCase());
        }

        if (field === "phone") {
            // Supprimer tout sauf les chiffres
            value = value.replace(/\D/g, "");
            // Supprimer un éventuel 0 au début
            if (value.startsWith("0")) value = value.slice(1);
            // Limiter à 9 chiffres max
            value = value.slice(0, 9);
            // Mettre à jour l'erreur si ≠ 9 chiffres (uniquement s'il y a une saisie)
            setPhoneError(value.length > 0 && value.length !== 9);
        }

        if (field === "email") {
            value = value.trim(); // Supprime les espaces autour
            setEmailError(value.length > 0 && !isValidEmail(value));
        }

        if (field === "newPswd") {
            value = value;
            setNewPswdError(value.length > 0 && !isStrongPassword(value));
            setConfirmPswdError(value !== formValues.confirmNewPswd && formValues.confirmNewPswd !== "");
        }

        if (field === "confirmNewPswd") {
            value = value;
            setConfirmPswdError(value !== formValues.newPswd);
        }

        setFormValues((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const isModified = (field) => formValues[field] !== initialValues[field];

    const handleModifyUser = () => {
        postAPI({
            requestType: APIRequestType.MODIFY_USER,
            email: formValues.email,
            phone: `0033${formValues.phone.replace(/^0/, "")}`, // Ajoute "0033" et enlève le 0 initial
            name: formValues.name,
            lastName: formValues.lastName,
            pswd: formValues.newPswd === "" ? null : formValues.newPswd,
            role: currentUserPro.role,
            status: "ACTIVE",
            userId: currentUserPro.id,
            updateParkList: false,
            pageRequest: "Profile"
        });
    };

    const handleReset = () => {
        setFormValues(initialValues);
        setEmailError(false);
        setPhoneError(false);
        setNewPswdError(false);
        setConfirmPswdError(false);
    };

    return (
        <PageLayout>
            <MDBox sx={{
                paddingTop: "15px", // espace en haut
                paddingLeft: "30px", // espace à gauche
                paddingRight: "30px", // espace à droite
            }}
            >
                <ProfileDashboardNavbar />
            </MDBox>
            <Header user={currentUserPro}>
                <MDBox mt={5}>
                    <Grid container spacing={1}>
                        <Grid item xs={24} md={12} xl={6} sx={{ display: "flex" }}>
                            <MDBox display="flex" flexDirection="column" width="100%" px={2}>
                                <MDBox display="flex" width="96%" mb={2} justifyContent="space-between">
                                    <MDBox width="48%">
                                        <MDInput
                                            type="text"
                                            label="Last Name"
                                            fullWidth
                                            value={formValues.lastName}
                                            inputProps={{ color: "dark" }}
                                            onChange={handleChange("lastName")}
                                            error={error}
                                            InputLabelProps={{
                                                shrink: true, // Force le label à rester au-dessus même si l'input est vide
                                            }}
                                            sx={{
                                                backgroundColor: isModified("lastName") ? "#c4e9fd" : "transparent",
                                                borderRadius: "5px",
                                            }}
                                        />
                                    </MDBox>
                                    <MDBox width="48%">
                                        <MDInput
                                            type="text"
                                            label="First Name"
                                            fullWidth
                                            value={formValues.name}
                                            inputProps={{ color: "dark" }}
                                            onChange={handleChange("name")}
                                            error={error}
                                            InputLabelProps={{
                                                shrink: true, // Force le label à rester au-dessus même si l'input est vide
                                            }}
                                            sx={{
                                                backgroundColor: isModified("name") ? "#c4e9fd" : "transparent",
                                                borderRadius: "5px",
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>
                                <MDBox mb={2} width="40%">
                                    <MDInput
                                        type="tel"
                                        label="Phone"
                                        fullWidth
                                        value={formValues.phone}
                                        onChange={handleChange("phone")}
                                        error={phoneError}
                                        helperText={phoneError ? "Invalid Format." : ""}
                                        inputProps={{ color: "dark", maxLength: 9 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: "1rem" }}>
                                                        +33
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            backgroundColor: isModified("phone") ? "#c4e9fd" : "transparent",
                                            borderRadius: "5px",
                                        }}
                                    />
                                </MDBox>
                                <MDBox mb={2} width="70%">
                                    <MDInput
                                        type="email"
                                        label="Email"
                                        fullWidth
                                        value={formValues.email}
                                        onChange={handleChange("email")}
                                        error={emailError}
                                        inputProps={{ color: "dark" }}
                                        InputLabelProps={{ shrink: true }}
                                        helperText={emailError ? "Invalid Format." : ""}
                                        sx={{
                                            backgroundColor: isModified("email") ? "#c4e9fd" : "transparent",
                                            borderRadius: "5px",
                                        }}
                                    />
                                </MDBox>
                                {(currentUserPro.role === "AdminAT" || currentUserPro.role === "AdminEnterprise") && (
                                    <>
                                        <MDBox display="flex" width="30%" justifyContent="space-between" alignItems="flex-start" ml={1.5} mt={2} mb={2}>
                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                                color="dark"
                                                fontSize="0.9rem"
                                            >
                                                {"Change your Password"}
                                            </MDTypography>
                                        </MDBox>
                                        <MDBox mb={2} width="55%">
                                            <MDInput
                                                type="password"
                                                label="New Password"
                                                fullWidth
                                                value={formValues.newPswd}
                                                onChange={handleChange("newPswd")}
                                                error={newPswdError}
                                                helperText={newPswdError ? "8 characters, number, upper case, lower case." : ""}
                                                inputProps={{ color: "dark" }}
                                                InputLabelProps={{ shrink: true }}
                                                sx={{
                                                    backgroundColor: isModified("newPswd") ? "#c4e9fd" : "transparent",
                                                    borderRadius: "5px",
                                                }}
                                            />
                                        </MDBox>
                                        <MDBox mb={2} width="55%">
                                            <MDInput
                                                type="password"
                                                label="Confirm New Password"
                                                fullWidth
                                                value={formValues.confirmNewPswd}
                                                onChange={handleChange("confirmNewPswd")}
                                                error={confirmPswdError}
                                                helperText={confirmPswdError ? "Password not similar." : ""}
                                                inputProps={{ color: "dark" }}
                                                InputLabelProps={{ shrink: true }}
                                                sx={{
                                                    backgroundColor: isModified("confirmNewPswd") ? "#c4e9fd" : "transparent",
                                                    borderRadius: "5px",
                                                }}
                                            />
                                        </MDBox>
                                    </>
                                )}
                                <MDBox display="flex" justifyContent="center" alignItems="center" gap={2} mt={2}>
                                    <MDButton
                                        type="submit"
                                        variant="gradient"
                                        color="info"
                                        onClick={handleModifyUser}
                                        fullWidth
                                        disabled={!isFormValid()}
                                        sx={{ width: '150px' }}
                                    >
                                        SAVE CHANGES
                                    </MDButton>
                                    <MDButton
                                        type="submit"
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleReset}
                                        fullWidth
                                        sx={{ width: '150px' }}
                                    >
                                        RESET
                                    </MDButton>
                                </MDBox>
                            </MDBox>
                        </Grid>
                        <Grid item xs={24} md={12} xl={5.8}>
                            <MDBox pt={2.5} pb={1}>
                                <Grid container spacing={4}>
                                    <Grid item xs={12}>
                                        <Card>
                                            <MDBox
                                                mx={2}
                                                mt={-3}
                                                py={1.5}
                                                px={2}
                                                variant="gradient"
                                                bgColor="info"
                                                borderRadius="lg"
                                                coloredShadow="info"
                                            >
                                                <MDTypography variant="h6" color="white">
                                                    Park Access
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox pt={3}>
                                                <DataTable
                                                    table={{
                                                        columns: [
                                                            { Header: "", accessor: "name", align: "left" },
                                                        ],
                                                        rows: currentUserParksPro.map((park) => ({
                                                            nameValue: park.name, // Stockage de la valeur simple (entier)

                                                            name: (
                                                                <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
                                                                    {park.name}
                                                                </MDTypography>
                                                            ),
                                                        })),
                                                    }}
                                                    rowsNumberPerPage={4}
                                                    isSorted={false}
                                                    entriesPerPage={false}
                                                    showTotalEntries={false}
                                                    noEndBorder
                                                />
                                            </MDBox>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </MDBox>
                        </Grid>
                    </Grid>
                    <MDBox pr={3} display="flex" justifyContent="flex-end">
                        <MDTypography fontSize="14px" color="text" fontWeight="regular">
                            (Versions) BackEnd : {backEndVersionPro} / FrontEnd : v1.6.0
                        </MDTypography>
                    </MDBox>
                </MDBox>
            </Header>
        </PageLayout >
    );
}

export default Overview;
